import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, differenceInDays } from 'date-fns';

const { FiMapPin, FiCalendar, FiDollarSign, FiCheckCircle, FiClock } = FiIcons;

const StayHistoryLog = ({ stays, properties }) => {
  const sortedStays = [...stays].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-indigo-500" /> Stay History Log
        </h3>
        <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded uppercase">
          {stays.length} Total Bookings
        </span>
      </div>

      <div className="relative pl-8 space-y-8 before:absolute before:inset-0 before:left-[15px] before:w-0.5 before:bg-gray-100 before:content-['']">
        {sortedStays.map((stay) => {
          const prop = properties.find(p => p.id === stay.propertyId);
          const duration = differenceInDays(parseISO(stay.endDate), parseISO(stay.startDate));
          
          return (
            <div key={stay.id} className="relative group">
              {/* Timeline Dot */}
              <div className={`absolute -left-[25px] top-1 w-4 h-4 rounded-full border-4 border-white shadow-sm z-10 transition-transform group-hover:scale-125 ${
                stay.status === 'Active' ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              
              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <h4 className="font-black text-gray-900 flex items-center gap-2">
                      <SafeIcon icon={FiMapPin} size={14} className="text-indigo-600" />
                      {prop?.name || 'Unknown Property'}
                    </h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight mt-1">
                      {prop?.address}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${
                      stay.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {stay.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-50">
                  <div>
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Duration</p>
                    <div className="flex items-center gap-1.5 text-xs font-black text-gray-700">
                      <SafeIcon icon={FiClock} size={12} className="text-indigo-400" />
                      {duration} Days
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Period</p>
                    <div className="flex items-center gap-1.5 text-xs font-black text-gray-700">
                      <SafeIcon icon={FiCalendar} size={12} className="text-indigo-400" />
                      {format(parseISO(stay.startDate), 'MMM yy')} - {format(parseISO(stay.endDate), 'MMM yy')}
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Revenue</p>
                    <div className="flex items-center gap-1.5 text-xs font-black text-indigo-700">
                      <SafeIcon icon={FiDollarSign} size={12} />
                      ${stay.totalRent.toLocaleString()}
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Notes</p>
                    <p className="text-[10px] text-gray-500 font-medium italic truncate" title={stay.review}>
                      "{stay.review}"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StayHistoryLog;